import viewpaths from "global/constants/ViewPathConstants";
import TOUR_IDS from "../tour-ids";
import {
  builderBulkSignersSteps,
  builderFieldsSteps,
  builderRecipientsSteps,
  builderSendSteps,
  builderUploadSteps,
  selfSignEditorSteps,
  selfSignUploadSteps,
} from "../steps/builder.steps";
import type { TourDefinition } from "../../types/tour.types";

type ChapterInput = Pick<
  TourDefinition,
  "id" | "name" | "description" | "steps" | "autoStartWhen"
>;

/** A chapter covers one stage of a builder flow, on that flow's own route. */
const chapterOn =
  (route: string) =>
  (tour: ChapterInput): TourDefinition => ({
    ...tour,
    route,
    version: 1,
    autoStart: true,
  });

const chapter = chapterOn(viewpaths.createSignRequestsViewPath);
const selfSignChapter = chapterOn(viewpaths.createSelfSignRequestPath);

export const builderUploadChapter = chapter({
  id: TOUR_IDS.BUILDER_UPLOAD,
  name: "Creating a request",
  description: "The four stages, and getting your document in.",
  autoStartWhen: "request.upload",
  steps: builderUploadSteps,
});

// Gated on a recipient row rather than the page around it: the screen seeds its
// first blank signer from an effect, so the page is on screen for a moment with
// no row, no signing-order toggle, and nothing for those steps to point at.
export const builderRecipientsChapter = chapter({
  id: TOUR_IDS.BUILDER_RECIPIENTS,
  name: "Adding recipients",
  description: "Who signs, in what order, and who approves first.",
  autoStartWhen: "request.recipientRow",
  steps: builderRecipientsSteps,
});

// Gated on a rendered PDF page, not the fields rail. The rail and the viewer's
// container both mount before the document paints, and an unpainted container
// has no height — so the step for the document itself, and the right-click
// reveal that has to land on a page, would both come up empty.
export const builderFieldsChapter = chapter({
  id: TOUR_IDS.BUILDER_FIELDS,
  name: "Placing fields",
  description: "Who a field belongs to, and how to put one on the page.",
  autoStartWhen: "request.documentPage",
  steps: builderFieldsSteps,
});

export const builderSendChapter = chapter({
  id: TOUR_IDS.BUILDER_SEND,
  name: "Before you send",
  description: "The message, the reminders, and when it goes out.",
  autoStartWhen: "request.emailContentHead",
  steps: builderSendSteps,
});

export const builderBulkSignersChapter = chapter({
  id: TOUR_IDS.BUILDER_BULK_SIGNERS,
  name: "Sending to many people",
  description:
    "Import the list, fix what's flagged, send everyone their own copy.",
  autoStartWhen: "bulk.signersBar",
  steps: builderBulkSignersSteps,
});

export const selfSignUploadChapter = selfSignChapter({
  id: TOUR_IDS.SELF_SIGN_UPLOAD,
  name: "Signing it yourself",
  description: "The two stages, and getting your document in.",
  autoStartWhen: "request.upload",
  steps: selfSignUploadSteps,
});

export const selfSignEditorChapter = selfSignChapter({
  id: TOUR_IDS.SELF_SIGN_EDITOR,
  name: "Filling and signing",
  description: "Place what you need on the page, then sign it.",
  autoStartWhen: "request.documentPage",
  steps: selfSignEditorSteps,
});

export const BUILDER_CHAPTERS: TourDefinition[] = [
  builderUploadChapter,
  builderRecipientsChapter,
  builderBulkSignersChapter,
  builderFieldsChapter,
  builderSendChapter,
  selfSignUploadChapter,
  selfSignEditorChapter,
];

export default BUILDER_CHAPTERS;
